import React from "react";
import { useParams } from "react-router-dom";
import { usePayout } from "@/_actions/researchActions";
import { Copy, Paperclip, CheckCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Dinero from "dinero.js";
import FileUploadButton from "@/components/forms/FileUploadButton";

export default function Detail() {
  const { id } = useParams();
  const { data, isLoading, error, mutate } = usePayout(id);

  const handleUploadSuccess = (s3Key, fileName, fileType) => {
    console.log(`${fileType} uploaded successfully:`, { s3Key, fileName });
    // Trigger a refetch of payout data to show updated invoice status
    mutate();
  };

  const handleUploadError = (error, fileType) => {
    console.error(`Error uploading ${fileType}:`, error);
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-xl p-6 space-y-3">
        <h3 className="text-zinc-800 text-xl font-bold leading-relaxed tracking-tight">
          Solicitud de pago {data.id}
        </h3>

        <ul className="text-center">
          <li>Subtotal: {new Dinero({ amount: data.base, currency: "MXN" }).toFormat()}</li>
          <li>IVA: {new Dinero({ amount: data.tax, currency: "MXN" }).toFormat()}</li>
          <li>Total: {new Dinero({ amount: data.total, currency: "MXN" }).toFormat()}</li>
        </ul>
      </section>

      {!data.pdf_invoice || !data.xml_invoice ? (
        <section className="bg-white rounded-xl p-6 space-y-3">
          <div className="flex">
            <div>
              <h3 className="text-zinc-800 text-xl font-bold leading-relaxed tracking-tight">
                Datos de facturación
              </h3>
              <p>Para poder procesar tu pago, necesitamos que subas la factura correspondiente.</p>
            </div>

            <div className="flex-grow" />
            <div>
              <Tooltip>
                <TooltipTrigger>
                  <Copy
                    onClick={(e) => {
                      e.preventDefault();
                      const content = document.getElementById("billing-info").innerHTML;
                      toast.success("Información de facturación copiada al portapapeles");
                      navigator.clipboard.writeText(content).catch((err) => {
                        $log.error("Something went wrong", err);
                      });
                    }}
                    className="text-zinc-600 hover:text-zinc-800 cursor-pointer"
                    size={20}
                  />
                </TooltipTrigger>
                <TooltipContent>Copiar información de facturación</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <h4 className="text-zinc-600 text-sm leading-relaxed tracking-tight">
            Emitir factura con los siguientes datos:*
          </h4>

          <div className="bg-stone-50 p-4 space-y-5">
            <p>
              Concepto: Comisión por localización de clientes
              <br />
              Código de SAT: 80141600
            </p>
            <p id="billing-info">
              MARKETING DIGITAL CASAS Y TERRENOS
              <br />
              LA PERPETUA 1668, JARDINES DEL COUNTRY
              <br />
              GUADALAJARA JALISCO. MÉXICO
              <br />
              CP. 44210
              <br />
              Concepto: Gastos General
            </p>
          </div>

          <div className="flex gap-3">
            {!data.pdf_invoice ? (
              <FileUploadButton
                payoutId={id}
                fileType="pdf"
                size="lg"
                variant="outline"
                acceptedFileTypes=".pdf,application/pdf"
                onUploadSuccess={(s3Key, fileName) => handleUploadSuccess(s3Key, fileName, "PDF")}
                onUploadError={(error) => handleUploadError(error, "PDF")}
              >
                Subir factura PDF
              </FileUploadButton>
            ) : (
              <Button disabled={true}>
                <CheckCircle className="inline-block ml-2" size={16} />
                Factura PDF subida
              </Button>
            )}
            {!data.xml_invoice ? (
              <FileUploadButton
                payoutId={id}
                fileType="xml"
                size="lg"
                variant="outline"
                acceptedFileTypes=".xml,application/xml,text/xml"
                onUploadSuccess={(s3Key, fileName) => handleUploadSuccess(s3Key, fileName, "XML")}
                onUploadError={(error) => handleUploadError(error, "XML")}
              >
                Subir factura XML
              </FileUploadButton>
            ) : (
              <Button disabled={true}>
                <CheckCircle className="inline-block ml-2" size={16} />
                Factura XML subida
              </Button>
            )}
          </div>
        </section>
      ) : null}

      {/* if all data is uploaded */}
      {data.pdf_invoice && data.xml_invoice && data.status === "pending" && (
        <section className="bg-white rounded-xl p-6 space-y-3">
          <h3 className="text-zinc-800 text-xl font-bold leading-relaxed tracking-tight">
            Pago en proceso
          </h3>
          <p>Tu pago está siendo procesado. Recibirás una notificación una vez que se complete.</p>
        </section>
      )}
    </div>
  );
}
